/**
 * AP-PAY-01: Finance Payment Detail
 * Route: /ap/payments/:paymentId (ap.invoice.view)
 *
 * Full payment detail: header, confirm, allocate against invoices, reverse.
 * Financial invariants enforced: no over-allocation, confirmed amounts only.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Check, CheckCircle2, Clock, XCircle, AlertTriangle,
  Plus, Trash2, RotateCcw, ChevronDown, Info,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_CFG = {
  INITIATED:            { label: "Initiated",            color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE" },
  PENDING_CONFIRMATION: { label: "Pending Confirmation", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A" },
  CONFIRMED:            { label: "Confirmed",            color: "#166534", bg: "#F0FDF4", border: "#BBF7D0" },
  REVERSED:             { label: "Reversed",             color: "#991B1B", bg: "#FEF2F2", border: "#FECACA" },
};

function StatusPill({ status }) {
  const c = STATUS_CFG[status] || { label: status, color: "#666", bg: "#F5F5F5", border: "#DDD" };
  return (
    <span style={{
      display: "inline-block", padding: "4px 14px", borderRadius: "20px",
      fontSize: "12px", fontWeight: 700, background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      {c.label}
    </span>
  );
}

export default function FinancePaymentDetail() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [payment,   setPayment]   = useState(null);
  const [allocs,    setAllocs]    = useState([]);
  const [loading,   setLoading]   = useState(true);

  // Confirm modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmForm, setConfirmForm] = useState({ confirmed_amount_minor: "", external_transaction_ref: "", confirmation_notes: "" });

  // Allocate modal
  const [showAllocate,    setShowAllocate]    = useState(false);
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [allocLines, setAllocLines] = useState([{ invoice_id: "", allocation_amount_minor: "" }]);

  // Reverse modal
  const [showReverse,   setShowReverse]   = useState(false);
  const [reverseReason, setReverseReason] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const hasCap    = (cap) => user?.capabilities?.includes(cap) || user?.capabilities?.includes("ap.*");
  const canRecord = hasCap("ap.payment.record");
  const canApprove = hasCap("ap.invoice.approve");

  const loadPayment = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/ap/payments/${paymentId}`, { withCredentials: true });
      setPayment(res.data.payment || res.data);
      setAllocs(res.data.allocations || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load payment");
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  useEffect(() => { loadPayment(); }, [loadPayment]);

  const loadApprovedInvoices = useCallback(async () => {
    if (!showAllocate) return;
    try {
      const res = await axios.get(`${API}/api/ap/match/queue`, {
        params: { status: "APPROVED", page_size: 50 },
        withCredentials: true,
      });
      setApprovedInvoices(res.data.items || []);
    } catch { /* non-blocking */ }
  }, [showAllocate]);

  useEffect(() => { loadApprovedInvoices(); }, [loadApprovedInvoices]);

  // ── Confirm payment ─────────────────────────────────────────────────────────
  const submitConfirm = async (e) => {
    e.preventDefault();
    const amt = parseInt(confirmForm.confirmed_amount_minor, 10);
    if (!amt || amt <= 0) return toast.error("Confirmed amount must be positive (minor units)");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/ap/payments/${paymentId}/confirm`, {
        confirmed_amount_minor: amt,
        external_transaction_ref: confirmForm.external_transaction_ref || undefined,
        confirmation_notes: confirmForm.confirmation_notes || undefined,
      }, { withCredentials: true });
      toast.success("Payment confirmed");
      setShowConfirm(false);
      loadPayment();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Confirmation failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Allocate payment ────────────────────────────────────────────────────────
  const submitAllocate = async (e) => {
    e.preventDefault();
    const lines = allocLines.filter(l => l.invoice_id && l.allocation_amount_minor);
    if (lines.length === 0) return toast.error("Add at least one allocation line");
    const parsed = lines.map(l => ({
      invoice_id: l.invoice_id,
      allocation_amount_minor: parseInt(l.allocation_amount_minor, 10),
    }));
    if (parsed.some(l => !l.allocation_amount_minor || l.allocation_amount_minor <= 0))
      return toast.error("Each allocation amount must be a positive integer (minor units)");

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/ap/payments/${paymentId}/allocate`, {
        allocations: parsed,
      }, { withCredentials: true });
      toast.success(`Allocated ${res.data.allocations_created} invoice(s) successfully`);
      setShowAllocate(false);
      setAllocLines([{ invoice_id: "", allocation_amount_minor: "" }]);
      loadPayment();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Allocation failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reverse payment ─────────────────────────────────────────────────────────
  const submitReverse = async (e) => {
    e.preventDefault();
    if (!reverseReason.trim()) return toast.error("Reversal reason is required");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/ap/payments/${paymentId}/reverse`, {
        reversal_reason: reverseReason,
      }, { withCredentials: true });
      toast.success("Payment reversed — all allocations released");
      setShowReverse(false);
      loadPayment();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reversal failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#9E8A6B" }}>Loading payment…</div>
  );
  if (!payment) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#9E8A6B" }}>Payment not found</div>
  );

  const isConfirmed  = payment.status === "CONFIRMED";
  const isReversed   = payment.status === "REVERSED";
  const canConfirm   = canRecord && !isConfirmed && !isReversed;
  const canAllocate  = canRecord && isConfirmed && !isReversed;
  const canReverse   = canApprove && !isReversed;
  const unallocated  = payment.unallocated_amount_minor ?? 0;

  return (
    <div data-testid="finance-payment-detail" style={{ minHeight: "100vh", background: "#F8F7F4" }}>

      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 20,
        background: "rgba(248,247,244,0.96)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid #E7D8C0", padding: "16px 24px",
        display: "flex", alignItems: "center", gap: "12px",
      }}>
        <button onClick={() => navigate("/ap/payments")}
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9E8A6B",
            display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}>
          <ArrowLeft size={14} /> Payments
        </button>
        <span style={{ color: "#D4C5A9" }}>/</span>
        <span style={{ fontWeight: 700, color: "#1C160C", fontSize: "14px" }}>
          {payment.ap_payment_reference}
        </span>
        <StatusPill status={payment.status} />

        <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          {canConfirm && (
            <button data-testid="confirm-payment-btn"
              onClick={() => { setConfirmForm({ confirmed_amount_minor: String(payment.payment_amount_minor), external_transaction_ref: payment.external_transaction_ref || "", confirmation_notes: "" }); setShowConfirm(true); }}
              style={actionBtnStyle("#166534")}>
              <Check size={13} /> Confirm Payment
            </button>
          )}
          {canAllocate && (
            <button data-testid="allocate-payment-btn"
              onClick={() => setShowAllocate(true)}
              style={actionBtnStyle("#1D4ED8")}>
              <Plus size={13} /> Allocate
            </button>
          )}
          {canReverse && !isReversed && (
            <button data-testid="reverse-payment-btn"
              onClick={() => setShowReverse(true)}
              style={actionBtnStyle("#991B1B")}>
              <RotateCcw size={13} /> Reverse
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto", display: "grid", gap: "20px" }}>

        {/* Payment Summary Card */}
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Payment Summary</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "16px" }}>
            {[
              { label: "AP Reference",      value: payment.ap_payment_reference },
              { label: "Supplier",           value: payment.supplier_name || payment.supplier_id },
              { label: "Payment Date",       value: fmtDate(payment.payment_date) },
              { label: "Method",             value: (payment.payment_method || "").replace("_", " ") },
              { label: "Recorded Amount",    value: fmt(payment.payment_amount_minor, payment.currency_code) },
              { label: "Confirmed Amount",   value: fmt(payment.confirmed_amount_minor, payment.currency_code) },
              { label: "Allocated",          value: fmt(payment.allocated_amount_minor, payment.currency_code) },
              { label: "Unallocated",        value: fmt(unallocated, payment.currency_code), highlight: unallocated > 0 },
              { label: "Recorded By",        value: payment.recorded_by },
              { label: "UTR / Ext Ref",      value: payment.external_transaction_ref || "—" },
            ].map(({ label, value, highlight }) => (
              <div key={label}>
                <div style={{ fontSize: "11px", color: "#9E8A6B", fontWeight: 600, marginBottom: "3px" }}>{label}</div>
                <div style={{ fontSize: "13px", color: highlight ? "#166534" : "#1C160C", fontWeight: highlight ? 700 : 400 }}>
                  {value || "—"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Allocations */}
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Invoice Allocations ({allocs.length})</h3>
          {allocs.length === 0 ? (
            <p style={{ color: "#9E8A6B", fontSize: "13px", margin: 0 }}>
              No allocations yet.
              {canAllocate && " Click 'Allocate' to assign this payment to approved invoices."}
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #E7D8C0" }}>
                  {["Invoice Ref", "Amount Allocated", "Partial?", "Allocated By", "Date", "Status"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: "11px", color: "#9E8A6B", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allocs.map((a, i) => (
                  <tr key={a._id || i} data-testid={`alloc-row-${i}`}
                    style={{ borderBottom: "1px solid #F3EDE3" }}>
                    <td style={{ padding: "10px 12px", color: "#1C160C", fontFamily: "monospace", fontSize: "12px" }}>
                      {a.ap_reference || a.invoice_id}
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: a.is_reversed ? "#9E8A6B" : "#166534" }}>
                      {fmt(a.allocation_amount_minor, a.currency_code)}
                      {a.is_reversed && <span style={{ fontSize: "10px", marginLeft: "6px", color: "#991B1B" }}>(REVERSED)</span>}
                    </td>
                    <td style={{ padding: "10px 12px", color: "#9E8A6B" }}>{a.is_partial ? "Yes" : "Full"}</td>
                    <td style={{ padding: "10px 12px", color: "#9E8A6B" }}>{a.allocated_by}</td>
                    <td style={{ padding: "10px 12px", color: "#9E8A6B" }}>{fmtDate(a.allocated_at)}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{
                        fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px",
                        background: a.is_reversed ? "#FEF2F2" : "#F0FDF4",
                        color: a.is_reversed ? "#991B1B" : "#166534",
                      }}>
                        {a.is_reversed ? "REVERSED" : "ACTIVE"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Audit Trail */}
        {payment.audit_events?.length > 0 && (
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>Audit Trail</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {payment.audit_events.map((ev, i) => (
                <div key={i} data-testid={`audit-row-${i}`}
                  style={{ display: "flex", gap: "10px", padding: "8px 0",
                    borderBottom: i < payment.audit_events.length - 1 ? "1px solid #F3EDE3" : "none" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#9E8A6B",
                    marginTop: "5px", flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#1C160C" }}>{ev.event_type}</div>
                    <div style={{ fontSize: "11px", color: "#9E8A6B" }}>{fmtDateTime(ev.timestamp)} · {ev.by}</div>
                    {ev.comment && <div style={{ fontSize: "11px", color: "#6B5B4A", marginTop: "2px" }}>{ev.comment}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ══ Confirm Modal ═══════════════════════════════════════════════════ */}
      {showConfirm && (
        <Modal title="Confirm Payment Settlement" onClose={() => setShowConfirm(false)} testId="confirm-modal">
          <p style={{ fontSize: "12px", color: "#9E8A6B", margin: "0 0 16px" }}>
            Record bank confirmation evidence. Only the confirmed amount becomes available for invoice allocation.
          </p>
          <form onSubmit={submitConfirm} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <label style={labelStyle}>Confirmed Amount (minor units) <span style={{ color: "#BA0517" }}>*</span></label>
              <input data-testid="confirm-amount-input" type="number" min="1" step="1"
                value={confirmForm.confirmed_amount_minor}
                onChange={(e) => setConfirmForm(f => ({ ...f, confirmed_amount_minor: e.target.value }))}
                style={inputStyle} required />
            </div>
            <div>
              <label style={labelStyle}>Bank UTR / Transaction Ref</label>
              <input data-testid="confirm-utr-input"
                value={confirmForm.external_transaction_ref}
                onChange={(e) => setConfirmForm(f => ({ ...f, external_transaction_ref: e.target.value }))}
                style={inputStyle} placeholder="Bank UTR or reference" />
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea data-testid="confirm-notes-input" rows={2}
                value={confirmForm.confirmation_notes}
                onChange={(e) => setConfirmForm(f => ({ ...f, confirmation_notes: e.target.value }))}
                style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <ModalFooter onCancel={() => setShowConfirm(false)} submitLabel="Confirm Payment"
              submitting={submitting} testId="confirm-submit-btn" />
          </form>
        </Modal>
      )}

      {/* ══ Allocate Modal ══════════════════════════════════════════════════ */}
      {showAllocate && (
        <Modal title="Allocate Payment to Invoices" onClose={() => setShowAllocate(false)} testId="allocate-modal">
          <p style={{ fontSize: "12px", color: "#9E8A6B", margin: "0 0 4px" }}>
            Unallocated: <strong style={{ color: "#166534" }}>{fmt(unallocated, payment.currency_code)}</strong>
          </p>
          <p style={{ fontSize: "11px", color: "#9E8A6B", margin: "0 0 16px" }}>
            Allocations are against APPROVED invoices only. Amounts in minor units.
          </p>
          <form onSubmit={submitAllocate} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {allocLines.map((line, i) => (
              <div key={i} data-testid={`alloc-line-${i}`}
                style={{ display: "grid", gridTemplateColumns: "1fr 160px 30px", gap: "8px", alignItems: "end" }}>
                <div>
                  <label style={labelStyle}>Invoice</label>
                  {approvedInvoices.length > 0 ? (
                    <select data-testid={`alloc-invoice-select-${i}`}
                      value={line.invoice_id}
                      onChange={(e) => {
                        const copy = [...allocLines];
                        copy[i] = { ...copy[i], invoice_id: e.target.value };
                        setAllocLines(copy);
                      }}
                      style={inputStyle} required>
                      <option value="">Select invoice…</option>
                      {approvedInvoices.map(inv => (
                        <option key={inv.id} value={inv.id}>
                          {inv.ap_reference} — {fmt(inv.total_minor, inv.currency_code)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input data-testid={`alloc-invoice-input-${i}`}
                      value={line.invoice_id}
                      onChange={(e) => {
                        const copy = [...allocLines]; copy[i].invoice_id = e.target.value; setAllocLines(copy);
                      }}
                      placeholder="Invoice ID" style={inputStyle} required />
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Amount (minor)</label>
                  <input data-testid={`alloc-amount-input-${i}`} type="number" min="1" step="1"
                    value={line.allocation_amount_minor}
                    onChange={(e) => {
                      const copy = [...allocLines]; copy[i].allocation_amount_minor = e.target.value; setAllocLines(copy);
                    }}
                    style={inputStyle} required />
                </div>
                {allocLines.length > 1 && (
                  <button type="button" onClick={() => setAllocLines(ls => ls.filter((_, j) => j !== i))}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "#BA0517", alignSelf: "flex-end", paddingBottom: "9px" }}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            <button type="button"
              onClick={() => setAllocLines(ls => [...ls, { invoice_id: "", allocation_amount_minor: "" }])}
              style={{ alignSelf: "flex-start", fontSize: "12px", color: "#1D4ED8", background: "transparent",
                border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
              <Plus size={12} /> Add Another Invoice
            </button>
            <ModalFooter onCancel={() => setShowAllocate(false)} submitLabel="Allocate"
              submitting={submitting} testId="allocate-submit-btn" />
          </form>
        </Modal>
      )}

      {/* ══ Reverse Modal ══════════════════════════════════════════════════ */}
      {showReverse && (
        <Modal title="Reverse Payment" onClose={() => setShowReverse(false)} testId="reverse-modal">
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px",
            padding: "12px", marginBottom: "16px", display: "flex", gap: "8px" }}>
            <AlertTriangle size={14} style={{ color: "#991B1B", marginTop: "2px", flexShrink: 0 }} />
            <span style={{ fontSize: "12px", color: "#991B1B" }}>
              This will release ALL active allocations for this payment.
              Invoice outstanding amounts will be restored. This action cannot be undone.
            </span>
          </div>
          <form onSubmit={submitReverse} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <label style={labelStyle}>Reversal Reason <span style={{ color: "#BA0517" }}>*</span></label>
              <textarea data-testid="reverse-reason-input" rows={3}
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                style={{ ...inputStyle, resize: "vertical" }} required />
            </div>
            <ModalFooter onCancel={() => setShowReverse(false)} submitLabel="Confirm Reversal"
              submitting={submitting} testId="reverse-submit-btn" danger />
          </form>
        </Modal>
      )}
    </div>
  );
}

// ── Shared small components ────────────────────────────────────────────────────

function Modal({ title, onClose, children, testId }) {
  return (
    <div data-testid={testId}
      style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
        justifyContent: "center", padding: "20px" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#FFF", borderRadius: "20px", width: "100%", maxWidth: "540px",
        padding: "28px", boxShadow: "0 24px 80px rgba(0,0,0,0.2)" }}
        onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: "0 0 16px", fontSize: "17px", fontWeight: 700, color: "#1C160C" }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, submitLabel, submitting, testId, danger = false }) {
  return (
    <div style={{ paddingTop: "12px", borderTop: "1px solid #F3EDE3",
      display: "flex", gap: "10px", justifyContent: "flex-end" }}>
      <button type="button" onClick={onCancel}
        style={{ padding: "9px 18px", borderRadius: "8px", border: "1px solid #D4C5A9",
          background: "transparent", color: "#6B5B4A", cursor: "pointer", fontSize: "13px" }}>
        Cancel
      </button>
      <button type="submit" disabled={submitting} data-testid={testId}
        style={{ padding: "9px 20px", borderRadius: "8px", border: "none",
          background: submitting ? "#9E8A6B" : (danger ? "#991B1B" : "#1C160C"),
          color: "#FFF", fontWeight: 600, fontSize: "13px",
          cursor: submitting ? "default" : "pointer" }}>
        {submitting ? "Processing…" : submitLabel}
      </button>
    </div>
  );
}

const cardStyle = {
  background: "#FFF", borderRadius: "16px", border: "1px solid #E7D8C0",
  padding: "24px",
};
const cardTitleStyle = {
  margin: "0 0 16px", fontSize: "14px", fontWeight: 700, color: "#1C160C",
};
const labelStyle = {
  display: "block", marginBottom: "5px", fontSize: "12px", fontWeight: 600, color: "#6B5B4A",
};
const inputStyle = {
  width: "100%", padding: "9px 12px", boxSizing: "border-box",
  border: "1px solid #D4C5A9", borderRadius: "8px",
  fontSize: "13px", color: "#1C160C", background: "#FDFAF5", outline: "none",
};
function actionBtnStyle(color) {
  return {
    display: "flex", alignItems: "center", gap: "6px",
    padding: "8px 14px", borderRadius: "8px", border: "none",
    background: color, color: "#FFF", fontSize: "12px",
    fontWeight: 600, cursor: "pointer",
  };
}
